import React from 'react';
import { ArrowUpDown } from 'lucide-react';
import type { SortConfig } from '../../../hooks/useSort';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { formatCurrency, formatDecimal } from '../../../utils/format';
import type { RoastedBeansReportRow, RoastedBeansReportSummary } from '../../../services/report.service';
import { useLanguage } from '../../../features/language/useLanguage';

interface RoastedBeansReportSectionProps {
  summary: RoastedBeansReportSummary;
  rows: RoastedBeansReportRow[];
  loading: boolean;
  error: string | null;
  currencySymbol: string;
  sortConfig: SortConfig<RoastedBeansReportRow> | null;
  onSort: (key: keyof RoastedBeansReportRow) => void;
  onRowClick: (itemId: string) => void;
}

export const RoastedBeansReportSection: React.FC<RoastedBeansReportSectionProps> = ({
  summary,
  rows,
  loading,
  error,
  currencySymbol,
  sortConfig,
  onSort,
  onRowClick,
}) => {
  const { t } = useLanguage();
  return (
    <section className="space-y-4" data-test-id="roasted-beans-report-section">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roastedBeans.totalItems')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(summary.totalItems, 0)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roastedBeans.totalRemainingKg')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatDecimal(summary.totalRemainingQuantity, 2)}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500">{t('reports.roastedBeans.totalStockValue')}</p>
          <p className="mt-1 text-xl font-semibold text-gray-900">{formatCurrency(summary.totalStockValue, currencySymbol)}</p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200" data-test-id="roasted-beans-report-table">
          <thead className="bg-gray-50">
            <tr>
              {[
                ['itemCode', t('reports.roastedBeans.table.itemCode')],
                ['itemName', t('reports.roastedBeans.table.itemName')],
                ['relatedBatch', t('reports.roastedBeans.table.relatedBatch')],
                ['remainingQuantity', t('reports.roastedBeans.table.remaining')],
                ['averageCost', t('reports.roastedBeans.table.avgCost')],
              ].map(([key, label]) => (
                <th
                  key={key}
                  className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer"
                  onClick={() => onSort(key as keyof RoastedBeansReportRow)}
                >
                  <div className="flex items-center gap-1">
                    <span>{label}</span>
                    {sortConfig?.key === key && <ArrowUpDown className="h-3.5 w-3.5" />}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={5}>
                  <LoadingSpinner text={t('reports.roastedBeans.loading')} size="sm" />
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={5} className="px-4 py-4 text-sm text-red-600">
                  {error}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-4 text-sm text-gray-500">
                  {t('reports.roastedBeans.empty')}
                </td>
              </tr>
            ) : (
              rows.map(row => (
                <tr
                  key={row.itemId}
                  className="cursor-pointer hover:bg-gray-50"
                  data-test-id={`roasted-beans-row-${row.itemId}`}
                  onClick={() => onRowClick(row.itemId)}
                >
                  <td className="px-4 py-3 text-sm text-gray-700">{row.itemCode}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{row.itemName}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{row.relatedBatch || t('settingsMcp.values.none')}</td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatDecimal(row.remainingQuantity, 2)} {row.unitOfMeasure}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-gray-900">
                    {formatCurrency(row.averageCost, currencySymbol)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};
